import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import AnxietyForm from "./AnxietyForm";
import {
    fetchAnxietyAssessment,
    updateAnxietyAssessment,
    fetchAnxietySymptomsMaster
} from "../../../store/slices/anxietySlice";

const EditAnxiety = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { currentAnxietyAssessment, anxietySymptomsMaster, isLoading } = useSelector(state => state.anxiety);

    const [formData, setFormData] = useState(null);

    // Fetch assessment and symptoms master on mount
    useEffect(() => {
        dispatch(fetchAnxietyAssessment(id));
        dispatch(fetchAnxietySymptomsMaster());
    }, [dispatch, id]);

    // Map assessment data to formData when loaded
    useEffect(() => {
        if (currentAnxietyAssessment) {
            // Map each symptom field to a form field
            const symptomFields = [
                'numbness_or_tingling', 'feeling_hot', 'wobbliness_in_legs', 'unable_to_relax',
                'fear_of_worst_happening', 'dizzy_or_lightheaded', 'heart_pounding_racing', 'unsteady',
                'terrified_or_afraid', 'nervous', 'feeling_of_choking', 'hands_trembling',
                'shaky_unsteady', 'fear_of_losing_control', 'difficulty_in_breathing', 'fear_of_dying',
                'scared', 'indigestion', 'faint_lightheaded', 'face_flushed', 'hot_cold_sweats'
            ];

            const mappedSymptoms = symptomFields.reduce((acc, field) => {
                acc[field] = currentAnxietyAssessment[field] || "";
                return acc;
            }, {});

            setFormData({
                case_id: currentAnxietyAssessment.case_id || "",
                patient_id: currentAnxietyAssessment.patient_id || "",
                client_name: currentAnxietyAssessment.client_name || "",
                client_dob: currentAnxietyAssessment.client_dob || "",
                assessment_date: currentAnxietyAssessment.assessment_date || "",
                diagnosis: currentAnxietyAssessment.diagnosis || "",
                attending_physician: currentAnxietyAssessment.attending_physician || "",
                assessed_by: currentAnxietyAssessment.assessed_by || "",
                ...mappedSymptoms
            });
        }
    }, [currentAnxietyAssessment]);

    const handleChange = (selectedOption, actionMeta) => {
        const name = actionMeta.name;
        const value = selectedOption?.value;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const calculateTotal = () => {
        const symptomFields = [
            'numbness_or_tingling', 'feeling_hot', 'wobbliness_in_legs', 'unable_to_relax',
            'fear_of_worst_happening', 'dizzy_or_lightheaded', 'heart_pounding_racing', 'unsteady',
            'terrified_or_afraid', 'nervous', 'feeling_of_choking', 'hands_trembling',
            'shaky_unsteady', 'fear_of_losing_control', 'difficulty_in_breathing', 'fear_of_dying',
            'scared', 'indigestion', 'faint_lightheaded', 'face_flushed', 'hot_cold_sweats'
        ];

        return symptomFields.reduce((total, field) => {
            return total + parseInt(formData[field] || 0, 10);
        }, 0);
    };

    const getInterpretation = (total) => {
        if (total <= 21) return "Low anxiety";
        if (total <= 35) return "Moderate anxiety";
        return "Potentially concerning levels of anxiety";
    };

    const validateForm = () => {
        const errors = [];
        if (!formData.client_name) errors.push("Client name is required.");
        // if (!formData.assessment_date) errors.push("Assessment date is required.");
        // if (!formData.assessed_by) errors.push("Assessed by is required.");

        const symptomFields = [
            'numbness_or_tingling', 'feeling_hot', 'wobbliness_in_legs', 'unable_to_relax',
            'fear_of_worst_happening', 'dizzy_or_lightheaded', 'heart_pounding_racing', 'unsteady',
            'terrified_or_afraid', 'nervous', 'feeling_of_choking', 'hands_trembling',
            'shaky_unsteady', 'fear_of_losing_control', 'difficulty_in_breathing', 'fear_of_dying',
            'scared', 'indigestion', 'faint_lightheaded', 'face_flushed', 'hot_cold_sweats'
        ];

        let allFilled = true;
        symptomFields.forEach(field => {
            if (!formData[field] && formData[field] !== 0) allFilled = false;
        });

        if (!allFilled) errors.push("Please complete all 21 anxiety symptoms.");

        if (errors.length > 0) {
            Swal.fire({
                title: "Required Fields",
                html: errors.map(e => `• ${e}<br/>`).join(""),
                icon: "warning",
            });
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const total = calculateTotal();
        const interpretation = getInterpretation(total);

        const payload = {
            ...formData,
            case_id: caseId,
            patient_id: patientId,
            total_score: total,
            anxiety_level: interpretation
        };

        try {
            await dispatch(updateAnxietyAssessment({ case_id: caseId, assessmentData: payload })).unwrap();
            Swal.fire({
                title: "Success!",
                text: "Anxiety assessment updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });
            // Stay on edit page
        } catch (err) {
            Swal.fire({
                title: "Error",
                text: typeof err === "object" ? JSON.stringify(err) : "Failed to update assessment",
                icon: "error",
            });
        }
    };

    if (!formData) return <div className="text-center mt-5">Loading...</div>;

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase04/anxiety/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <AnxietyForm
                                formData={formData}
                                handleChange={handleChange}
                                anxietySymptomsMaster={anxietySymptomsMaster}
                                isLoading={isLoading}
                            />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                        {isLoading ? "Updating..." : (<><i className="ri-edit-2-line me-1"></i> Update</>)}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditAnxiety;
